import React from 'react';
import { Card, CardProps } from '@wix/design-system';
import { observer } from 'mobx-react-lite';
import { SkeletonCard } from '../SkeletonCard';
import { ErrorCard, ErrorCardProps } from '../ErrorCard';
import { useWixPatternsContainer } from '@wix/bex-core/react';

export interface CardContainerProps extends CardProps {
  /**
   * Additional loading flag, if you need to show loader after the main entity is fetched.
   * Also, if an error occurs on a side request (other than passed to `fetch`), you can use `error` to show the card error state.
   * Alongside with `error` you also need to pass `errorState.onRetry` callback, which should change the `status` accordingly.
   */
  status?: 'idle' | 'loading' | 'error' | 'success';
  /**
   * Height of content while loading (not including paddings)
   */
  minHeight?: string;
  /**
   * Card Error State configuration
   */
  errorState?: ErrorCardProps;
}

export function _CardContainer(props: CardContainerProps) {
  const { status, minHeight, children, errorState, ...rest } = props;
  const { translate: t } = useWixPatternsContainer();

  if (status === 'idle' || status === 'loading') {
    return <SkeletonCard minHeight={minHeight} {...rest} />;
  }

  if (status === 'error') {
    return (
      <ErrorCard
        title={
          errorState?.title ??
          t('cairo.entityPage.widgetError.TPAconnectionFail.title')
        }
        subtitle={
          errorState?.subtitle ??
          t('cairo.entityPage.widgetError.TPAconnectionFail.description')
        }
        retryText={
          errorState?.retryText ??
          t('cairo.entityPage.widgetError.TPAconnectionFail.CTA')
        }
        onRetry={errorState?.onRetry}
        {...rest}
      >
        {children}
      </ErrorCard>
    );
  }

  return <Card {...rest}>{children}</Card>;
}

_CardContainer.displayName = 'CardContainer';

export const CardContainer = observer(_CardContainer);
